import { useEffect, useState } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";

export type ScreenAwakeStatus = "trying" | "on" | "off" | "unavailable";

interface NativeScreenAwake {
  setEnabled(options: { enabled: boolean }): Promise<{ active: boolean }>;
}

const nativeAwake = registerPlugin<NativeScreenAwake>("MBScreenAwake");

/** Keep the display awake only while this reader is visible and enabled. */
export function useScreenAwake(initialEnabled = true): {
  enabled: boolean;
  status: ScreenAwakeStatus;
  setEnabled: (enabled: boolean) => void;
} {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [status, setStatus] = useState<ScreenAwakeStatus>(initialEnabled ? "trying" : "off");

  useEffect(() => {
    let disposed = false;
    let lock: WakeLockSentinel | null = null;
    let requestNumber = 0;
    const native = Capacitor.isNativePlatform();

    async function acquire() {
      const number = ++requestNumber;
      if (!enabled || document.visibilityState !== "visible") {
        setStatus("off");
        return;
      }
      if (lock) {
        void lock.release();
        lock = null;
      }
      setStatus("trying");
      try {
        if (native) {
          const result = await nativeAwake.setEnabled({ enabled: true });
          if (!disposed && number === requestNumber) setStatus(result.active ? "on" : "unavailable");
        } else if (navigator.wakeLock?.request) {
          const nextLock = await navigator.wakeLock.request("screen");
          if (disposed || number !== requestNumber) {
            await nextLock.release();
            return;
          }
          lock = nextLock;
          nextLock.addEventListener("release", () => {
            if (!disposed && number === requestNumber) setStatus("off");
          }, { once: true });
          setStatus("on");
        } else {
          setStatus("unavailable");
        }
      } catch {
        if (!disposed && number === requestNumber) setStatus("unavailable");
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        void acquire();
      } else {
        requestNumber++;
        if (lock) {
          void lock.release();
          lock = null;
        }
        if (native) void nativeAwake.setEnabled({ enabled: false }).catch(() => undefined);
        setStatus("off");
      }
    }

    if (enabled) void acquire();
    else {
      setStatus("off");
      if (native) void nativeAwake.setEnabled({ enabled: false }).catch(() => undefined);
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      requestNumber++;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (lock) void lock.release();
      if (native) void nativeAwake.setEnabled({ enabled: false }).catch(() => undefined);
    };
  }, [enabled]);

  return { enabled, status, setEnabled };
}
